import type { DropAnimation, DraggableSyntheticListeners } from '@dnd-kit/core'
import {
  defaultDropAnimationSideEffects,
  DndContext as DndKitDndContext,
  DragOverlay as DndKitDragOverlay,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext as DndSortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import type { CSSProperties, ReactNode } from 'react'
import React, { createContext, useContext, useMemo } from 'react'
import { CSS } from '@dnd-kit/utilities'
import { DragIcon } from '@fourthwall/icons'
import * as TableUI from '../TableUI'
import { useTableBody } from '../contexts/TableBodyContext'
import * as S from '../styled'
import { Icon } from '../../../common/Icon'

interface DndContextProps extends React.ComponentProps<typeof DndKitDndContext> {
  children: React.ReactNode
}

export const DndContext = ({ children, ...props }: DndContextProps) => {
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  return (
    <DndKitDndContext sensors={sensors} {...props}>
      {children}
    </DndKitDndContext>
  )
}

export interface SortableContextProps extends React.ComponentProps<typeof DndSortableContext> {
  children: React.ReactNode
}

export const SortableContext = ({ children, ...props }: SortableContextProps) => {
  return <DndSortableContext {...props}>{children}</DndSortableContext>
}

export interface TableSortableBodyContextProps {
  attributes: Record<string, any>
  listeners: DraggableSyntheticListeners
  ref(node: HTMLElement | null): void
}

export const TableSortableItemContext = createContext<TableSortableBodyContextProps>({
  attributes: {},
  listeners: undefined,
  ref() {},
})

export const TableSortableBodyTr = ({
  children,
  id,
  isDragged,
  isSelected,
}: {
  children: ReactNode
  id: string
  isDragged?: boolean
  isSelected?: boolean
}) => {
  const { setDropdownKey } = useTableBody()
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id })
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  )
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <TableSortableItemContext.Provider value={context}>
      <S.TableBodyTr
        $isDragged={isDragged}
        $isSelected={isSelected}
        ref={setNodeRef}
        style={style}
        onMouseLeave={() => {
          setDropdownKey((key) => key + 1)
        }}
      >
        {children}
      </S.TableBodyTr>
    </TableSortableItemContext.Provider>
  )
}

export const TableRowDragHandle = () => {
  const { attributes, listeners, ref } = useContext(TableSortableItemContext)

  return (
    <S.TableRowDragHandle {...attributes} {...listeners} ref={ref}>
      <Icon component={DragIcon} height={16} />
    </S.TableRowDragHandle>
  )
}

export const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
}

export const DragOverlay = ({ children }: { children: React.ReactNode }) => {
  return <DndKitDragOverlay dropAnimation={dropAnimationConfig}>{children}</DndKitDragOverlay>
}

export const TableSortableBody = TableUI.Body
export const TableSortableBodyTd = TableUI.BodyTd
